<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  kind: {
    type: String,
    required: true,
  },
  kindLabel: {
    type: String,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const idInstance = ref('');
const apiTokenInstance = ref('');
const apiUrl = ref('');

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const v$ = useVuelidate(
  {
    idInstance: { required },
    apiTokenInstance: { required },
    apiUrl: { required },
  },
  { idInstance, apiTokenInstance, apiUrl }
);

async function createChannel() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      channel: {
        type: 'green_api',
        kind: props.kind,
        id_instance: idInstance.value,
        api_token_instance: apiTokenInstance.value,
        api_url: apiUrl.value,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inbox.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="
        t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.TITLE', { kind: kindLabel })
      "
      :header-content="
        t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.DESC', { kind: kindLabel })
      "
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.idInstance.$error }">
          {{ t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.ID_INSTANCE.LABEL') }}
          <input
            v-model="idInstance"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.ID_INSTANCE.PLACEHOLDER')
            "
            @blur="v$.idInstance.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.apiTokenInstance.$error }">
          {{ t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.API_TOKEN_INSTANCE.LABEL') }}
          <input
            v-model="apiTokenInstance"
            type="text"
            :placeholder="
              t(
                'INBOX_MGMT.ADD.GREEN_API_CHANNEL.API_TOKEN_INSTANCE.PLACEHOLDER'
              )
            "
            @blur="v$.apiTokenInstance.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.apiUrl.$error }">
          {{ t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.API_URL.LABEL') }}
          <input
            v-model="apiUrl"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.API_URL.PLACEHOLDER')
            "
            @blur="v$.apiUrl.$touch"
          />
        </label>
        <p class="help-text">
          {{ t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.API_URL.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="t('INBOX_MGMT.ADD.GREEN_API_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
